import { Color3, Color4, DynamicTexture, HemisphericLight, type Mesh, MeshBuilder, PointLight, type Scene, StandardMaterial, TransformNode, Vector3 } from "@babylonjs/core";
import type { WalkSpace } from "../engine/player";
import { ROOM, SHELF, SHELF_Z, TURNTABLE } from "./layout";

/** The room's floor is flat at y = 0 and needs no streaming (spec 2o §4.2). */
export const ROOM_SPACE: WalkSpace = { height: () => 0, update: () => {} };

export interface BuiltRoom {
  root: TransformNode;
  signs: { prev: Mesh; next: Mesh };
  emptySign: Mesh;
  turntableTop: Vector3;
}

/** The fixed room every owner gets (spec 2o §2.2, §4.3): floor, walls, light, shelf, turntable and signs. */
export function buildRoom(scene: Scene): BuiltRoom {
  scene.clearColor = new Color4(0.05, 0.05, 0.06, 1);
  const root = new TransformNode("room", scene);
  const hemi = new HemisphericLight("room:hemi", new Vector3(0, 1, 0), scene);
  hemi.intensity = 0.6;
  hemi.diffuse = Color3.FromHexString("#ffe8cc");
  hemi.groundColor = Color3.FromHexString("#3a2c22");
  const lamp = new PointLight("room:lamp", new Vector3(0, ROOM.height - 0.4, 0.5), scene);
  lamp.intensity = 0.8;
  lamp.diffuse = Color3.FromHexString("#ffd9a8");

  const mat = (name: string, hex: string): StandardMaterial => {
    const m = new StandardMaterial(name, scene);
    m.diffuseColor = Color3.FromHexString(hex);
    m.specularColor = Color3.Black();
    return m;
  };
  const box = (name: string, w: number, h: number, d: number, x: number, y: number, z: number, m: StandardMaterial, collide: boolean): Mesh => {
    const b = MeshBuilder.CreateBox(name, { width: w, height: h, depth: d }, scene);
    b.position.set(x, y, z);
    b.material = m;
    b.checkCollisions = collide;
    b.isPickable = false;
    b.parent = root;
    return b;
  };

  const { width: W, depth: D, height: H, wall: T } = ROOM;
  box("room:floor", W, 0.1, D, 0, -0.05, 0, mat("room:wood", "#8a6242"), true);
  box("room:ceiling", W, 0.1, D, 0, H + 0.05, 0, mat("room:plaster", "#efe6da"), false);
  const wallMat = mat("room:wall", "#d9c7b0");
  box("room:wall-n", W + 2 * T, H, T, 0, H / 2, D / 2 + T / 2, wallMat, true);
  box("room:wall-s", W + 2 * T, H, T, 0, H / 2, -D / 2 - T / 2, wallMat, true);
  box("room:wall-e", T, H, D, W / 2 + T / 2, H / 2, 0, wallMat, true);
  box("room:wall-w", T, H, D, -W / 2 - T / 2, H / 2, 0, wallMat, true);

  // The shelf collides as one box; its boards and sides are only for the eye.
  const shelfW = SHELF.cols * SHELF.cell, shelfH = SHELF.bottom + SHELF.rows * SHELF.cell;
  box("room:shelf-collider", shelfW + 0.1, shelfH, SHELF.depth, 0, shelfH / 2, SHELF_Z, wallMat, true).isVisible = false;
  const boardMat = mat("room:board", "#6b4a32");
  for (let r = 0; r <= SHELF.rows; r++) box(`room:board-${r}`, shelfW + 0.1, 0.04, SHELF.depth, 0, SHELF.bottom + r * SHELF.cell - 0.02, SHELF_Z, boardMat, false);
  for (const s of [-1, 1]) box(`room:side-${s}`, 0.05, shelfH, SHELF.depth, s * (shelfW / 2 + 0.05), shelfH / 2, SHELF_Z, boardMat, false);

  const tt = MeshBuilder.CreateCylinder("room:turntable", { diameter: TURNTABLE.radius * 2, height: TURNTABLE.height }, scene);
  tt.position.set(TURNTABLE.x, TURNTABLE.height / 2, TURNTABLE.z);
  tt.material = mat("room:turntable", "#3d3a36");
  tt.checkCollisions = true;
  tt.isPickable = false;
  tt.parent = root;

  const front = D / 2 - 0.02; // just off the north wall
  const prev = label(scene, "room:sign-prev", ["◀ 上一頁"], 0.7, 0.3);
  prev.position.set(-(shelfW / 2 + 0.55), 1.3, front);
  const next = label(scene, "room:sign-next", ["下一頁 ▶"], 0.7, 0.3);
  next.position.set(shelfW / 2 + 0.55, 1.3, front);
  prev.metadata = { roomSign: -1 };
  next.metadata = { roomSign: 1 };
  prev.isPickable = next.isPickable = true;
  const emptySign = label(scene, "room:empty", ["還沒有上傳的模型", "請你的 agent 用 MCP create_upload 上傳 GLB"], 3.2, 0.9);
  emptySign.position.set(0, 1.3, SHELF_Z - SHELF.depth / 2 - 0.02);
  emptySign.isPickable = false;
  for (const m of [prev, next, emptySign]) m.parent = root;
  emptySign.setEnabled(false);

  return { root, signs: { prev, next }, emptySign, turntableTop: new Vector3(TURNTABLE.x, TURNTABLE.height, TURNTABLE.z) };
}

/** A plane facing the player (-z) with lines of text; the first line is bold. */
function label(scene: Scene, name: string, lines: string[], w: number, h: number): Mesh {
  const plane = MeshBuilder.CreatePlane(name, { width: w, height: h }, scene);
  const tex = new DynamicTexture(`${name}:tex`, { width: 1024, height: Math.round((1024 * h) / w) }, scene, true);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  const { width, height } = tex.getSize();
  ctx.fillStyle = "#f4e9d8";
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "#2b1d12";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const step = height / (lines.length + 1);
  lines.forEach((text, i) => {
    ctx.font = `${i === 0 ? "bold " : ""}${Math.round(step * (i === 0 ? 0.55 : 0.4))}px sans-serif`;
    ctx.fillText(text, width / 2, step * (i + 1));
  });
  tex.update();
  const m = new StandardMaterial(`${name}:mat`, scene);
  m.diffuseTexture = tex;
  m.emissiveColor = new Color3(0.55, 0.55, 0.55);
  m.specularColor = Color3.Black();
  plane.material = m;
  return plane;
}
